import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TrashAltIcon } from 'assets/icons';
import { FileUpload } from 'components/FileUpload';
import {
  DELETING_FILE,
  FILE,
  FILE_FETCHED,
  FILE_FORMAT_ERROR,
  FILE_REPLACED,
  getDescriptions,
  MAX_SIZE_ERROR,
  ModalStatusType,
  NO_FILE,
  REPLACING_FILE,
  UPLOADING_FILE,
} from 'components/FileUpload/paramsByState';
import {
  deleteFileBamCommercialFamilyCodesRequestSaga,
  getBamCommercialFamilyCodesFileInformation,
  getBamCommercialFamilyCodesFileInformationForRequest,
  getBamCommercialFamilyCodesFileModalStatus,
  getBamCommercialFamilyCodesFileUploadStatus,
  setBamCommercialFamilyCodesFileInformationForRequest,
  setBamCommercialFamilyCodesFileModalStatus,
  setBamCommercialFamilyCodesFileUploadStatus,
  uploadFileBamCommercialFamilyCodesRequestSaga,
} from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.store';

import { theme } from 'styles';
import { Box, Flex, Icon, MarginBox, Text } from 'UI';

const BamCommercialFamilyCodesFileUpload = ({ bamCommercialFamilyCodes }: { bamCommercialFamilyCodes: string[] }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fileInfoFetched = useSelector(getBamCommercialFamilyCodesFileInformation);
  const fileInfoRequest = useSelector(getBamCommercialFamilyCodesFileInformationForRequest);

  const modalStatus = useSelector(getBamCommercialFamilyCodesFileModalStatus);
  const fileStatus = useSelector(getBamCommercialFamilyCodesFileUploadStatus);

  const enableDelete =
    modalStatus === FILE &&
    fileStatus !== UPLOADING_FILE &&
    fileStatus !== REPLACING_FILE &&
    fileStatus !== DELETING_FILE;

  const setModalStatus = (status: ModalStatusType) => {
    dispatch(setBamCommercialFamilyCodesFileModalStatus(status));
  };

  const validateFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setModalStatus(FILE_FORMAT_ERROR);
      return false;
    } else if (file.size > 2000 * 1024) {
      setModalStatus(MAX_SIZE_ERROR);
      return false;
    } else {
      setModalStatus(FILE);
      return true;
    }
  };

  const uploadFile = (fileName: string, _fileSize: number, fileBase64: string) => {
    dispatch(
      uploadFileBamCommercialFamilyCodesRequestSaga({
        fileName,
        fileBase64,
      }),
    );
    dispatch(setBamCommercialFamilyCodesFileInformationForRequest({ fileName }));
  };

  const handleNoFile = () => {
    setModalStatus(NO_FILE);
    dispatch(setBamCommercialFamilyCodesFileUploadStatus(NO_FILE));
  };

  const handleDelete = () => {
    if (bamCommercialFamilyCodes.length > 0) {
      dispatch(deleteFileBamCommercialFamilyCodesRequestSaga());
    } else {
      setModalStatus(NO_FILE);
      dispatch(setBamCommercialFamilyCodesFileUploadStatus(NO_FILE));
    }
  };

  const getText1Override = (modalStatusType: ModalStatusType) => {
    switch (modalStatusType) {
      case FILE_FORMAT_ERROR: {
        return t(
          'backoffice.bam_commercial_family_codes.upload_file.file_error.format_error',
          "Sorry, looks like you've uploaded a non-compatible file",
        );
      }
      case MAX_SIZE_ERROR: {
        return t('common.file_upload.image.max_size_error', 'File size exceeds 2 MB');
      }
      default:
        return '';
    }
  };

  return (
    <>
      <FileUpload
        template={{
          link: '/fileTemplates/commercial_family_codes_Motrio_BAM.csv',
          fileName: 'Commercial family codes Motrio BAM.csv',
        }}
        handleFileBase64={uploadFile}
        validateFile={validateFile}
        handleNoFile={handleNoFile}
        status={modalStatus}
        descriptions={getDescriptions(t, modalStatus, { size: 2, unit: 'MB' })}
        getText1Override={getText1Override}
        currentFile={
          modalStatus === FILE
            ? {
                fileType: 'CSV',
                fileName:
                  fileStatus === FILE_FETCHED || fileStatus === FILE_REPLACED || fileStatus === DELETING_FILE
                    ? fileInfoFetched.fileName
                    : fileInfoRequest.fileName,
                fileBase64: '',
              }
            : undefined
        }
        isProcessing={fileStatus === REPLACING_FILE || fileStatus === DELETING_FILE}
      />
      {enableDelete && (
        <MarginBox mt={15}>
          <Flex>
            <Icon IconComponent={TrashAltIcon} color={theme.color.red} size={16} mt={2} onClick={handleDelete} />
            <Box width={10} />
            <Text type="base" bold color="red" cursor={'pointer'} onClick={handleDelete}>
              {t('common.file_upload.file_delete', 'Delete file')}
            </Text>
          </Flex>
        </MarginBox>
      )}
    </>
  );
};

export default BamCommercialFamilyCodesFileUpload;
